// client/src/pages/ArticleAdmin.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import '../styles/ArticleAdmin.css';

// Composants SVG inline
const IconEdit = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconDelete = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

const IconEye = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ArticleAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback pour les articles sans image
  const defaultImage = '/images/article-default.png';
  
  // Charger la liste des articles
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        
        // Construire les paramètres de requête
        const params = new URLSearchParams();
        params.append('page', currentPage);
        params.append('limit', 10);
        
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        
        if (categoryFilter) {
          params.append('categorie', categoryFilter);
        }
        
        if (publishedFilter !== '') {
          params.append('publie', publishedFilter);
        }
        
        const response = await axios.get(`/api/articles?${params.toString()}`);
        setArticles(response.data.articles);
        setTotalPages(response.data.totalPages);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des articles:', err);
        setError('Erreur lors du chargement des articles');
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [currentPage, searchTerm, categoryFilter, publishedFilter]);
  
  // Supprimer un article
  const handleDeleteArticle = async (id, titre) => {
    // Demander confirmation
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'article "${titre}" ?`)) {
      return;
    }
    
    try {
      await axios.delete(`/api/articles/${id}`);
      
      // Mettre à jour la liste des articles
      setArticles(articles.filter(article => article._id !== id));
      
      // Afficher un message de succès
      setSuccessMessage(`L'article "${titre}" a été supprimé avec succès`);
      
      // Masquer le message après 3 secondes
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'article:', err);
      setError(`Erreur lors de la suppression: ${err.response?.data?.message || err.message}`);
      
      // Masquer le message d'erreur après 3 secondes
      setTimeout(() => {
        setError(null);
      }, 3000);
    }
  };
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, articleId) => {
    if (!originalUrl || imageErrors[articleId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (articleId) => {
    console.log(`Erreur de chargement d'image pour l'article ${articleId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [articleId]: true
    }));
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non publié';
    
    try {
      return format(new Date(dateString), 'dd/MM/yyyy à HH:mm', { locale: fr });
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };
  
  // Formater la catégorie
  const formatCategory = (category) => {
    const categories = {
      'actualite': 'Actualité',
      'analyse': 'Analyse',
      'interview': 'Interview',
      'evenement': 'Événement'
    };
    
    return categories[category] || category;
  };
  
  // Catégories disponibles  
  const categories = [
  { id: '', label: 'Toutes les catégories' },
  { id: 'actualite', label: 'Actualités' },
  { id: 'analyse', label: 'Analyses' },
  { id: 'interview', label: 'Interviews' },
  { id: 'evenement', label: 'Événements' },
  { id: 'infographies', label: 'Infographies' },
  { id: 'leon14', label: 'Leon XIV' }
];
  
  
  // Statuts de publication
  const publishedOptions = [
    { id: '', label: 'Tous les statuts' },
    { id: 'true', label: 'Publiés' },
    { id: 'false', label: 'Brouillons' }
  ];
  
  // Gestionnaire de recherche
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Revenir à la première page lors d'une recherche
  };
  
  // Gestionnaire de changement de filtre
  const handleFilterChange = (e, setFilter) => {
    setFilter(e.target.value);
    setCurrentPage(1); // Revenir à la première page lors d'un changement de filtre
  };
  
  // Pagination - Page précédente
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Pagination - Page suivante
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  return (
    <div className="article-admin-page">
      <div className="article-admin-header">
        <h1>Gestion des Articles</h1>
        <Link to="/admin/articles/create" className="create-article-button">
          + Nouvel Article
        </Link>
      </div>
      
      {/* Messages de succès ou d'erreur */}
      {successMessage && <div className="message success">{successMessage}</div>}
      {error && <div className="message error">{error}</div>}
      
      {/* Filtre et recherche */}
      <div className="article-admin-filters">
        <form onSubmit={handleSearch} className="article-search-form">
          <div className="search-input-container">
            <IconSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="article-search-input"
            />
          </div>
          <button type="submit" className="article-search-button">
            Rechercher
          </button>
        </form>
        
        <div className="article-filters">
          <select
            value={categoryFilter}
            onChange={(e) => handleFilterChange(e, setCategoryFilter)}
            className="article-filter-select"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
          
          <select
            value={publishedFilter}
            onChange={(e) => handleFilterChange(e, setPublishedFilter)}
            className="article-filter-select"
          >
            {publishedOptions.map(option => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Liste des articles */}
      {loading ? (
        <div className="article-admin-loading">Chargement des articles...</div>
      ) : articles.length === 0 ? (
        <div className="article-admin-no-results">
          {searchTerm || categoryFilter || publishedFilter !== '' ? 
            "Aucun article ne correspond aux critères de recherche" : 
            "Aucun article disponible"}
        </div>
      ) : (
        <div className="article-admin-table-container">
          <table className="article-admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Titre</th>
                <th>Catégorie</th>
                <th>Date de publication</th>
                <th>Statut</th>
                <th>Mis en avant</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(article => (
                <tr key={article._id}>
                  <td className="article-admin-image-cell">
                    <img 
                      src={getApiImageUrl(article.image, article._id)} 
                      alt={article.titre}
                      className="article-admin-thumbnail"
                      onError={() => handleImageError(article._id)}
                    />
                  </td>
                  <td>{article.titre}</td>
                  <td>{formatCategory(article.categorie)}</td>
                  <td>{formatDate(article.datePublication)}</td>
                  <td>
                    <span className={`article-status ${article.publie ? 'published' : 'draft'}`}>
                      {article.publie ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td>
                    <span className={`featured-status ${article.featured ? 'is-featured' : 'not-featured'}`}>
                      {article.featured ? 'Oui' : 'Non'}
                    </span>
                  </td>
                  <td>
                    <div className="article-actions">
                      <a 
                        href={`/actualites/${article.slug}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="view-button"
                        title="Voir l'article"
                      >
                        <IconEye />
                      </a>
                      <Link 
                        to={`/admin/articles/edit/${article._id}`}
                        className="edit-button"
                        title="Modifier l'article"
                      >
                        <IconEdit />
                      </Link>
                      <button
                        onClick={() => handleDeleteArticle(article._id, article.titre)}
                        className="delete-button"
                        title="Supprimer l'article"
                      >
                        <IconDelete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="article-admin-pagination">
          <button 
            onClick={goToPreviousPage} 
            disabled={currentPage === 1}
            className="pagination-button"
          >
            &laquo; Précédent
          </button>
          <span className="pagination-info">
            Page {currentPage} sur {totalPages}
          </span>
          <button 
            onClick={goToNextPage} 
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Suivant &raquo;
          </button>
        </div>
      )}
    </div>
  );
};

export default ArticleAdmin;